// Shared definition of the selectable risk-metric windows, so the watchlist detail and
// the compare view offer the same choices and read the same fields. The windows are
// computed server-side from the daily history already fetched (1Y / 3Y / 5Y / 10Y / max),
// so switching window is free on the client.

import type { RiskMetrics, WindowedRiskMetrics } from "../types";

export type RiskWindowKey = "one_year" | "three_year" | "five_year" | "ten_year" | "max";

export interface RiskWindow {
  key: RiskWindowKey;
  label: string;
}

// Shared fallback for users who have not saved a display preference yet.
export const DEFAULT_RISK_WINDOW: RiskWindowKey = "one_year";

// Order shown in the selector (shortest to longest, max last).
export const RISK_WINDOWS: RiskWindow[] = [
  { key: "one_year", label: "1Y" },
  { key: "three_year", label: "3Y" },
  { key: "five_year", label: "5Y" },
  { key: "ten_year", label: "10Y" },
  { key: "max", label: "Max" },
];

export function isRiskWindowKey(value: unknown): value is RiskWindowKey {
  return RISK_WINDOWS.some((window) => window.key === value);
}

/** The RiskMetrics for a given window, or undefined/null when absent. */
export function riskForWindow(
  windowed: WindowedRiskMetrics | undefined | null,
  key: RiskWindowKey,
): RiskMetrics | undefined | null {
  if (!windowed) return undefined;
  return windowed[key];
}

/** Whether a window actually has computed metrics (enough history). */
export function hasWindow(metrics: RiskMetrics | undefined | null): boolean {
  return !!metrics && metrics.window_days != null;
}
